import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe, TranslatePipe } from '@skylab-angular-shared/pipes';
import { NumericValueAccessorDirective, SelectValueAccessorDirective, ValidateDirective, ValidateInnerDirective } from '@skylab-angular-shared/ui';
import { CurrencyItem } from '@skylab-shared/business';
import { DiscountKind, LabeledEnumValue, SaleDocumentItemModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';

type DiscountCellItem = {
    label: string,
    value: DiscountKind,
    displayValue(): string
};

@Component({
    selector: 'skl-document-item-discount-cell',
    template: `
    @if (visible) {
        <ins-form-field class="no-boundary grid-cell-ins-component">
            <div class="display--flex">
                <div class="align--tiny-right">
                    <ins-input
                        inputmode="decimal"
                        [(ngModel)]="discountValue"
                        [disabled]="adjusted"
                        [precision]="precision"
                        [sklValidate]="'discountKind.' + entity.sun">
                    </ins-input>
                </div>
                <div>
                    <ins-select
                        #select
                        display-text="label"
                        [disabled]="adjusted"
                        [converter]="discountKindConverter"
                        [(ngModel)]="discountKind"
                        [sklValidate]="'discountValue.' + entity.sun">
                        <span
                            slot="content"
                            class="align--tiny-right"
                            [sklValidateInner]="{key: 'discountValue.' + entity.sun, option: 'disable'}"
                            [sklValidateInnerReadonly]="adjusted">
                            {{ getComboboxSelectedValue() }}
                        </span>
                        @for(item of items; track item.label) {
                            <ins-option
                                [value]="item">
                                <span>
                                    {{item.displayValue()}} {{item.label}}
                                </span>
                            </ins-option>
                        }
                    </ins-select>
                </div>
            </div>
        </ins-form-field>
    } @else {
        <ins-form-field
            class="no-boundary">
            <ins-input
                [nullable]="true"
                [disabled]="adjusted"
                [placeholder]="'ui.placeholders.empty' | translate"
                inputmode="decimal"
                [sklValidate]="'discountKind.' + entity.sun">
            </ins-input>
        </ins-form-field>
    }
    `,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [NumericValueAccessorDirective, FormsModule, ValidateDirective, SelectValueAccessorDirective, ValidateInnerDirective, TranslatePipe]
})
export class DocumentItemDiscountCellComponent {
    @Input({required: true}) public entity!: SaleDocumentItemModel;
    @Input({required: true}) public currency!: CurrencyItem;
    @Input() public adjusted = false;
    @Input() public nullable = false;
    @ViewChild('select') private select!: ElementRef<HTMLInsSelectElement>;
 
    private internalDiscountValue!: number;
    private internalDiscountKind!: DiscountKind;
 
    public visible = true;
 
    private changeListener!: Function;
 
    public constructor(
        private readonly elementRef: ElementRef,
        private readonly currencyPipe: CurrencyPipe
    ) { }
 
    public ngOnInit() {
        if (this.nullable) {
            this.visible = this.entity.discountValue != null;
        }
 
        this.internalDiscountKind = this.entity.discountKind;
        this.setInternalDiscountValue();
 
        this.changeListener = this.entity.onPropertyChanged(args => {
            if (args.propertyName === 'discountKind') {
                this.internalDiscountKind = this.entity.discountKind || DiscountKind.Percentage;
            } else if (args.propertyName === 'discountValue') {
                this.setInternalDiscountValue();
            }
        });
    }
 
    public ngOnDestroy(): void {
        this.changeListener?.();
    }
 
    public ngAfterViewInit(): void {
        this.select?.nativeElement.setPopoverAnchor(this.elementRef.nativeElement);
    }
 
    public getComboboxSelectedValue(): string {
        return this.discountKind === DiscountKind.Percentage ? '%' : this.currency.symbol;
    }
 
    public setInternalDiscountValue(): void {
        this.internalDiscountValue = this.entity.discountKind === DiscountKind.Percentage
            ? Calc.round(this.entity.discountValue * 100, 6, MidpointRounding.AwayFromZero)
            : Calc.round(this.entity.discountValue, this.currency.precision, MidpointRounding.AwayFromZero);
    }
 
    public set discountValue(val: number) {
        this.entity.discountValue = this.discountKind === DiscountKind.Quota
            ? Calc.round(val, this.currency.precision, MidpointRounding.AwayFromZero)
            : Calc.round(val / 100, 8, MidpointRounding.AwayFromZero);
 
        this.internalDiscountValue = val;
    }
 
    public get discountValue(): number {
        return this.internalDiscountValue;
    }
 
    public set discountKind(val: DiscountKind) {
        this.entity.discountKind = val;
        this.internalDiscountKind = val;
        // eslint-disable-next-line no-self-assign
        this.discountValue = this.discountValue;
    }
 
    public get discountKind(): DiscountKind {
        return this.internalDiscountKind;
    }
 
    public get items(): DiscountCellItem[] {
        return [
            {
                label: '%',
                value: DiscountKind.Percentage,
                displayValue: () => `${this.discountValue}`
            },
            {
                label: this.currency.symbol,
                value: DiscountKind.Quota,
                displayValue: () => this.currencyPipe.transform(this.discountValue, {precision: this.currency.precision})
            },
        ];
    }
 
    public get discountKindConverter() {
        const self = this;
        return {
            convert(value: DiscountKind): LabeledEnumValue {
                return value === DiscountKind.Percentage
                    ? { value: DiscountKind.Percentage, label: '%' }
                    : { value: DiscountKind.Quota, label: self.currency.symbol };
            },
            convertBack({ value }: LabeledEnumValue): DiscountKind {
                return <DiscountKind>value;
            }
        };
    }
 
    public get precision(): number | null {
        return this.internalDiscountKind === DiscountKind.Percentage ? null : this.currency.precision;
    }
}
